import React, { useState, useEffect, useRef } from 'react';
import { APIProvider, Map } from '@vis.gl/react-google-maps';
import MapComponent from './Map';
import api from '../services/api';
import '../styles/CreatePost.css';

const debounce = (func, delay) => {
    let id;
    return (...args) => { clearTimeout(id); id = setTimeout(() => func.apply(null, args), delay); };
};

const BtnSpinner = () => <span className="cp-btn-spinner" aria-hidden="true" />;
const AiSpinner  = () => <span className="cp-ai-spinner"  aria-hidden="true" />;

const CreatePost = () => {
    const [content,       setContent]       = useState('');
    const [location,      setLocation]      = useState(null);
    const [radius,        setRadius]        = useState(1000);
    const [aiSuggestions, setAiSuggestions] = useState(null);
    const [isAnalyzing,   setIsAnalyzing]   = useState(false);
    const [isSubmitting,  setIsSubmitting]  = useState(false);
    const [submitError,   setSubmitError]   = useState('');
    const [locationError, setLocationError] = useState('');
    const [posted,        setPosted]        = useState(false);

    useEffect(() => {
        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by your browser.');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            ()    => setLocationError(
                'Location access is required to create a post. Please enable it and refresh.'
            )
        );
    }, []);

    const analyzePostContent = async (text) => {
        if (text.trim().length < 5) { setAiSuggestions(null); return; }
        setIsAnalyzing(true);
        try {
            const res = await api.post('/posts/analyze-content', { content: text });
            setAiSuggestions(res.data);
        } catch {
            setAiSuggestions(null);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const debouncedAnalyze = useRef(debounce((text) => analyzePostContent(text), 500)).current;

    const handleContentChange = (e) => {
        setContent(e.target.value);
        debouncedAnalyze(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!location) return;
        setIsSubmitting(true);
        setSubmitError('');
        try {
            await api.post('/posts', {
                content,
                location: { type: 'Point', coordinates: [location.lng, location.lat] },
                radius,
                ai_categorization: aiSuggestions,
            });
            setPosted(true);
            setContent('');
            setAiSuggestions(null);
        } catch (err) {
            setSubmitError(
                err.response?.data?.message || 'Failed to create post — please try again.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setPosted(false);
        setContent('');
        setAiSuggestions(null);
        setSubmitError('');
    };

    // ── Early returns ─────────────────────────────────────────────────────────
    if (!location && !locationError) {
        return <div className="create-loading">Getting your location…</div>;
    }

    if (locationError) {
        return (
            <div className="create-post">
                <div className="location-error" role="alert">{locationError}</div>
            </div>
        );
    }

    if (posted) {
        return (
            <div className="create-post">
                <div className="create-success">
                    <span className="create-success-icon">✅</span>
                    <div className="create-success-text">
                        <p className="create-success-title">Post created!</p>
                        <p className="create-success-sub">
                            Your post is now visible to neighbours in your area.
                        </p>
                    </div>
                    <button className="create-another-btn" onClick={resetForm}>
                        Create another →
                    </button>
                </div>
            </div>
        );
    }

    // ── Form ──────────────────────────────────────────────────────────────────
    const showAiFeedback = isAnalyzing || !!aiSuggestions;
    const typeKey = (aiSuggestions?.post_type_suggestion || 'general').toLowerCase();

    return (
        <div className="create-post">
            <h1 className="create-post-title">Create a Post</h1>

            <form className="create-post-form" onSubmit={handleSubmit} noValidate>

                {/* ── Content textarea ── */}
                <div className="cp-field">
                    <div className="textarea-label-row">
                        <label htmlFor="cp-content" className="cp-label">
                            Post content
                        </label>
                        {isAnalyzing && (
                            <span className="ai-status">
                                <AiSpinner /> Analysing…
                            </span>
                        )}
                    </div>
                    <textarea
                        id="cp-content"
                        className="create-textarea"
                        value={content}
                        onChange={handleContentChange}
                        placeholder="What's happening in your area? (e.g., I need to borrow a ladder)"
                        rows={4}
                        required
                        disabled={isSubmitting}
                    />
                </div>

                {/* ── AI feedback ── */}
                {showAiFeedback && (
                    <div className="ai-feedback-container">
                        {isAnalyzing && !aiSuggestions && (
                            <span className="ai-status">
                                <AiSpinner /> Analysing your post…
                            </span>
                        )}
                        {aiSuggestions && (
                            <>
                                <p className="ai-suggestions-header">🤖 AI Suggestions</p>

                                <div className="ai-row">
                                    <span className="ai-row-label">Type</span>
                                    <span className={`badge badge--${typeKey}`}>
                                        {aiSuggestions.post_type_suggestion || 'General'}
                                    </span>
                                </div>

                                <div className="ai-row">
                                    <span className="ai-row-label">Category</span>
                                    <span className="badge badge--category">
                                        {aiSuggestions.category || 'General'}
                                    </span>
                                </div>

                                {aiSuggestions.tags?.length > 0 && (
                                    <div className="ai-row">
                                        <span className="ai-row-label">Tags</span>
                                        <div className="ai-tags">
                                            {aiSuggestions.tags.map(tag => (
                                                <span key={tag} className="badge badge--general">
                                                    #{tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ── Map + radius pill ── */}
                <div className="cp-field">
                    <label className="cp-label">Reach radius</label>
                    <div className="map-wrap">
                        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                            <Map
                                defaultCenter={location}
                                defaultZoom={14}
                                mapId={process.env.REACT_APP_MAP_ID}
                                style={{ height: '380px', width: '100%' }}
                            >
                                <MapComponent
                                    location={location}
                                    radius={radius}
                                    onRadiusChange={setRadius}
                                    onLocationChange={setLocation}
                                />
                            </Map>
                        </APIProvider>
                        <div className="radius-pill">{Math.round(radius)}m radius</div>
                    </div>
                </div>

                {submitError && (
                    <div className="submit-error" role="alert">{submitError}</div>
                )}

                <button
                    type="submit"
                    className="cp-submit-btn"
                    disabled={isAnalyzing || isSubmitting || !content.trim()}
                >
                    {isSubmitting
                        ? <><BtnSpinner /> Creating…</>
                        : isAnalyzing
                        ? 'Analysing…'
                        : 'Create Post'}
                </button>

            </form>
        </div>
    );
};

export default CreatePost;
